/**
 * Featured collection — tabs, countdown, and infinite carousel.
 * Carousel uses the exact same transform-driven infinite loop as
 * `section-collection-list.js`.
 */
if (!customElements.get('lion-featured-collection-tabs')) {
(function () {
const TRANSITION_MS = 450;

/* ─────────────────────────── Tabs ─────────────────────────── */
class FeaturedCollectionTabs extends HTMLElement {
  connectedCallback() {
    this.tabs = Array.from(this.querySelectorAll('.featured-collection__tab'));
    this.panels = Array.from(this.querySelectorAll('.featured-collection__panel'));
    if (this.tabs.length === 0) return;
    this.tabs.forEach((t) => {
      t.addEventListener('click', (e) => this.switchTab(e.currentTarget));
      t.addEventListener('keydown', (e) => this.onKeydown(e));
    });
  }

  onKeydown(e) {
    const current = this.tabs.findIndex((t) => t.getAttribute('aria-selected') === 'true');
    let next = current;
    if (e.key === 'ArrowRight') next = (current + 1) % this.tabs.length;
    else if (e.key === 'ArrowLeft') next = (current - 1 + this.tabs.length) % this.tabs.length;
    else if (e.key === 'Home') next = 0;
    else if (e.key === 'End') next = this.tabs.length - 1;
    else return;
    e.preventDefault();
    this.switchTab(this.tabs[next]);
    this.tabs[next].focus();
  }

  switchTab(targetTab) {
    const index = Number(targetTab.dataset.index);
    this.tabs.forEach((t) => {
      const active = Number(t.dataset.index) === index;
      t.setAttribute('aria-selected', active ? 'true' : 'false');
      t.setAttribute('tabindex', active ? '0' : '-1');
    });
    this.panels.forEach((p) => {
      const active = Number(p.dataset.tabIndex) === index;
      p.classList.toggle('visually-hidden', !active);
      if (active) {
        const carousel = p.querySelector('lion-featured-collection-carousel');
        if (carousel && typeof carousel.handleResize === 'function') {
          carousel.handleResize();
        }
      }
    });
  }
}
customElements.define('lion-featured-collection-tabs', FeaturedCollectionTabs);

/* ──────────────────────── Countdown ──────────────────────── */
class FeaturedCollectionCountdown extends HTMLElement {
  connectedCallback() {
    const target = new Date(this.dataset.target).getTime();
    if (Number.isNaN(target)) return;
    this.label = this.dataset.label || '';
    this.render(target);
    this.timer = setInterval(() => this.render(target), 1000);
  }

  disconnectedCallback() {
    clearInterval(this.timer);
  }

  _escapeHtml(str) {
    const div = document.createElement('div');
    div.textContent = str;
    return div.innerHTML;
  }

  render(target) {
    const diff = target - Date.now();
    if (diff <= 0) {
      this.innerHTML = '';
      clearInterval(this.timer);
      return;
    }
    const d = Math.floor(diff / 86400000);
    const h = Math.floor((diff % 86400000) / 3600000);
    const m = Math.floor((diff % 3600000) / 60000);
    const s = Math.floor((diff % 60000) / 1000);
    const label = this.label ? `<span class="featured-collection__countdown-label">${this._escapeHtml(this.label)}</span>` : '';
    this.innerHTML = label +
      `<span class="featured-collection__countdown-unit"><span class="featured-collection__countdown-value">${d}</span>d</span>` +
      `<span class="featured-collection__countdown-unit"><span class="featured-collection__countdown-value">${String(h).padStart(2, '0')}</span>h</span>` +
      `<span class="featured-collection__countdown-unit"><span class="featured-collection__countdown-value">${String(m).padStart(2, '0')}</span>m</span>` +
      `<span class="featured-collection__countdown-unit"><span class="featured-collection__countdown-value">${String(s).padStart(2, '0')}</span>s</span>`;
  }
}
customElements.define('lion-featured-collection-countdown', FeaturedCollectionCountdown);

/* ──────────────────────── Carousel ──────────────────────── */
class FeaturedCollectionCarousel extends HTMLElement {
  connectedCallback() {
    this.track = this.querySelector('[data-featured-collection-track]');
    this.grid = this.track?.querySelector('.featured-collection__grid');
    this.prevBtn = this.querySelector('[data-featured-collection-prev]');
    this.nextBtn = this.querySelector('[data-featured-collection-next]');
    this.counter = this.querySelector('[data-featured-collection-counter]');
    if (!this.track || !this.grid) return;

    this.originalItems = Array.from(this.grid.children);
    this.originalCount = this.originalItems.length;
    if (this.originalCount === 0) return;

    this.itemWidth = 0;
    this.gap = 0;
    this.step = 0;
    this.originalWidth = 0;
    this.position = 0;

    this.cloneItems();

    this.prevBtn?.addEventListener('click', () => this.goPrev());
    this.nextBtn?.addEventListener('click', () => this.goNext());
    this.grid.addEventListener('transitionend', (e) => this.onTransitionEnd(e));
    this.resizeObserver = new ResizeObserver(() => this.handleResize());
    this.resizeObserver.observe(this.track);

    this.computeMetrics();
    this.applyTransform(false);
    this.updateCounter();

    // Trascinamento con dito e mouse: il carosello si muove con transform e
    // overflow hidden, quindi senza questo il gesto non produrrebbe nulla.
    window.lionTrascinamentoCarosello?.(this);
  }

  disconnectedCallback() {
    this.resizeObserver?.disconnect();
  }

  cloneItems() {
    this.originalItems.forEach((el) => {
      const clone = el.cloneNode(true);
      clone.setAttribute('aria-hidden', 'true');
      clone.dataset.clone = 'true';
      this.grid.appendChild(clone);
    });
  }

  computeMetrics() {
    if (this.originalItems.length === 0) return;
    // Una scheda puo' non essere disegnata affatto: succede quando il prodotto
    // e' escluso dalle collezioni, dove il <li> resta vuoto e il CSS lo
    // nasconde. Misurando quello la larghezza risultava zero e il passo
    // diventava il solo spazio fra le schede, otto pixel: il carosello si
    // muoveva di un'inezia a ogni gesto. Si misura la prima scheda che
    // esiste davvero.
    const riferimento = Array.from(this.originalItems).find(
      (elemento) => elemento.getBoundingClientRect().width > 0
    );
    if (!riferimento) return;
    this.itemWidth = riferimento.getBoundingClientRect().width;
    const style = window.getComputedStyle(this.grid);
    this.gap = parseFloat(style.columnGap || style.gap) || 0;
    this.step = this.itemWidth + this.gap;
    const firstClone = this.grid.children[this.originalCount];
    this.originalWidth = firstClone ? firstClone.offsetLeft : this.originalCount * this.step;
  }

  handleResize() {
    this.computeMetrics();
    if (!this.step) return;
    const index = Math.round(this.position / this.step);
    this.position = index * this.step;
    this.applyTransform(false);
  }

  applyTransform(animate) {
    this.grid.style.transition = animate ? `transform ${TRANSITION_MS}ms ease` : 'none';
    this.grid.style.transform = `translate3d(${-this.position}px, 0, 0)`;
  }

  goNext() {
    if (this.position >= this.originalWidth) {
      this.position -= this.originalWidth;
      this.applyTransform(false);
      this.forceReflow();
    }
    this.position += this.step;
    this.applyTransform(true);
    this.updateCounter();
  }

  goPrev() {
    if (this.position <= 0) {
      this.position += this.originalWidth;
      this.applyTransform(false);
      this.forceReflow();
    }
    this.position -= this.step;
    this.applyTransform(true);
    this.updateCounter();
  }

  forceReflow() {
    void this.grid.offsetWidth;
  }

  onTransitionEnd(event) {
    if (event.propertyName !== 'transform') return;
    if (this.position >= this.originalWidth) {
      this.position -= this.originalWidth;
      this.applyTransform(false);
    } else if (this.position < 0) {
      this.position += this.originalWidth;
      this.applyTransform(false);
    }
  }

  updateCounter() {
    if (!this.counter) return;
    const count = this.originalCount;
    const index = Math.round(this.position / this.step);
    const display = ((index % count) + count) % count + 1;
    this.counter.textContent = `${display} / ${count}`;
  }
}
customElements.define('lion-featured-collection-carousel', FeaturedCollectionCarousel);
})();
}
